import React, { memo, useCallback, Fragment, useEffect } from 'react';
import { ListMatchingWidgetForm, ListMatchingBlockVariation, ListMatchingItemForm } from './ListMatchingBlock';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Label, Row, Col } from 'reactstrap';
import { FormInput } from '@core/components/form';
import { FormHTML } from '@core/components/form/FormHTML';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { SortableList } from '@modules/common/Sortable';

export interface ListMatchingBlockSettingsProps {
  form: ListMatchingWidgetForm;
  variation: ListMatchingBlockVariation;
}

export const ListMatchingBlockSettings = memo((props: ListMatchingBlockSettingsProps) => {
  const { form, variation } = props;
  const listMatching = useObservable(form.controls.items.items$, []);
  const listMatchingValues = useObservable(form.controls.items.value$, []);

  useEffect(() => {
    if (listMatching.length === 0) {
      form.controls.items.push();
    }
  }, [form, listMatching]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newListMatching = R.move(listMatchingValues, oldIndex, newIndex);
      form.patchValue({
        items: newListMatching,
      });
    },
    [form, listMatchingValues],
  );

  return (
    <div>
      <Fragment>
        <Row className="ml-0">
          <Col md={9}>
            <FormHTML autoFocus minHeight={300} control={form.controls.instructions} />
          </Col>
        </Row>
        {!!listMatching.length && <Label className='ml-0'>List Matching Items</Label>}
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {listMatching.map((ctrl, index) => {
            return <SortableListMatchingItem key={index} index={index} ctrl={ctrl} form={form} i={index} />;
          })}
        </SortableList>
      </Fragment>
    </div>
  );
});

interface ListMatchingItemProps {
  i: number;
  ctrl: ListMatchingItemForm;
  form: ListMatchingWidgetForm;
}

const ListItem = memo((props: ListMatchingItemProps) => {
  const { ctrl, form, i } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical font-size-16 mr-3 mb-3" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <Row key={`item-${i}`} className="ml-0">
        <Col md={5} className="input-width">
          <DragHandle />
          <FormInput control={ctrl.controls.item} type="textarea" />
        </Col>
        <Col md={5}>
          <FormInput control={ctrl.controls.matchingItem} type="textarea" />
        </Col>
        <Col md={2}>
          {!i && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.items.push()}>
              Add Item
            </a>
          )}
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.items.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
      </Row>
    </div>
  );
});

export const SortableListMatchingItem = SortableElement(ListItem);